/**
 * Service de formatage et helpers pour les garanties
 */

import {
  WARRANTY_TYPE_LABELS,
  VEHICLE_TYPE_LABELS,
  DURATION_LABELS,
  WARRANTY_TYPES,
  VEHICLE_TYPES,
  type Warranty,
  type WarrantyListItem,
  type DurationOption,
} from '../types/warranty.types';

export const formatWarrantyDisplay = (warranty: Warranty | WarrantyListItem): string => {
  const name = warranty.name;
  const duration = warranty.duration;
  const price = warranty.sellingPrice;

  const durationLabel = DURATION_LABELS[duration as DurationOption] || `${duration} mois`;
  const priceFormatted = formatPrice(price);

  return `${name} (${durationLabel} - ${priceFormatted})`;
};

export const formatWarrantyShortDisplay = (warranty: Warranty | WarrantyListItem): string => {
  return warranty.name;
};

export const getWarrantyTypeLabel = (warrantyType: number): string => {
  return WARRANTY_TYPE_LABELS[warrantyType] || 'Inconnu';
};

export const getVehicleTypeLabel = (vehicleType: number): string => {
  return VEHICLE_TYPE_LABELS[vehicleType] || 'Inconnu';
};

export const getDurationLabel = (duration: number): string => {
  return DURATION_LABELS[duration as DurationOption] || `${duration} mois`;
};

export const formatPrice = (amount: number, decimals = 2): string => {
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(amount);
};

export const formatPriceHT = (amount: number): string => {
  return `${formatPrice(amount)} HT`;
};

export const formatPriceTTC = (amount: number): string => {
  return `${formatPrice(amount)} TTC`;
};

export const formatDuration = (months: number): string => {
  if ([12, 24, 48, 60].includes(months)) {
    return `${months} mois`;
  }

  if (months < 12) {
    return `${months} mois`;
  }

  const years = months / 12;
  if (years === 1) {
    return '1 an';
  }

  return `${years} ans`;
};

export const formatDurationFull = (months: number): string => {
  if (months < 12) {
    return `${months} mois`;
  }

  const years = months / 12;
  const yearText = years === 1 ? '1 an' : `${years} ans`;

  return `${months} mois (${yearText})`;
};

export const getWarrantyStatusBadge = (warranty: Warranty | WarrantyListItem): {
  label: string;
  color: 'success' | 'warning' | 'error' | 'info';
  variant: 'filled' | 'outlined';
} => {
  if (!warranty) {
    return {
      label: 'Inconnu',
      color: 'error',
      variant: 'outlined',
    };
  }

  if (!warranty.isActive) {
    return {
      label: 'Inactive',
      color: 'error',
      variant: 'outlined',
    };
  }

  return {
    label: 'Active',
    color: 'info',
    variant: 'outlined',
  };
};

export const getWarrantyTypeBadgeColor = (warrantyType: number): 'primary' | 'secondary' => {
  return warrantyType === WARRANTY_TYPES.WARRANTY ? 'primary' : 'secondary';
};

export const getVehicleTypeBadgeColor = (vehicleType: number): 'default' | 'info' | 'warning' => {
  switch (vehicleType) {
    case VEHICLE_TYPES.STANDARD:
      return 'default';
    case VEHICLE_TYPES.COLLECTION:
      return 'warning';
    case VEHICLE_TYPES.TWO_WHEELER:
      return 'info';
    default:
      return 'default';
  }
};

export const getWarrantySummary = (warranty: Warranty | WarrantyListItem): string => {
  const parts: string[] = [
    getWarrantyTypeLabel(warranty.warrantyType),
    getDurationLabel(warranty.duration),
    formatPriceTTC(warranty.sellingPrice),
  ];

  if ('kmLimit' in warranty && warranty.kmLimit) {
    parts.push(`${warranty.kmLimit.toLocaleString('fr-FR')} km`);
  }

  if ('financialCap' in warranty) {
    parts.push(`Plafond: ${formatPrice(warranty.financialCap)}`);
  }

  return parts.join(' • ');
};

export const formatWarrantyCode = (code: string | undefined): string => {
  if (!code) return 'N/A';
  return code.toUpperCase();
};

export const isRecentWarranty = (warranty: Warranty | WarrantyListItem): boolean => {
  if (!warranty.createdAt) return false;

  const createdDate = new Date(warranty.createdAt);
  const now = new Date();
  const diffInDays = (now.getTime() - createdDate.getTime()) / (1000 * 60 * 60 * 24);

  return diffInDays <= 7;
};

export const formatCreatedDate = (dateString: string): string => {
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('fr-FR', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(date);
};

export const formatCreatedDateRelative = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffInSeconds = (now.getTime() - date.getTime()) / 1000;

  if (diffInSeconds < 60) {
    return 'À l\'instant';
  }

  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 60) {
    return `Il y a ${diffInMinutes} minute${diffInMinutes > 1 ? 's' : ''}`;
  }

  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) {
    return `Il y a ${diffInHours} heure${diffInHours > 1 ? 's' : ''}`;
  }

  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 30) {
    return `Il y a ${diffInDays} jour${diffInDays > 1 ? 's' : ''}`;
  }

  const diffInMonths = Math.floor(diffInDays / 30);
  if (diffInMonths < 12) {
    return `Il y a ${diffInMonths} mois`;
  }

  const diffInYears = Math.floor(diffInMonths / 12);
  return `Il y a ${diffInYears} an${diffInYears > 1 ? 's' : ''}`;
};

export const formatWarrantyType = getWarrantyTypeLabel;
export const formatVehicleType = getVehicleTypeLabel;
